// src/pages/SevenAmLibraryEp0.jsx
// 7AM Library — Episodio 0: El cerebro que decide sin avisarte
// Página pública, sin autenticación requerida.

import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Headphones,
  BookOpen,
  Lightbulb,
  HelpCircle,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import SevenAmSubscribe from '../components/SevenAmSubscribe';

// ─────────────────────────────────────────────────────────────────────────────
// REPRODUCTOR DE AUDIO
// ─────────────────────────────────────────────────────────────────────────────
function AudioPlayer({ src }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setPlaying(!playing);
  };
  const toggleMute = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !muted;
    setMuted(!muted);
  };
  const handleTimeUpdate = () => { if (audioRef.current) setCurrentTime(audioRef.current.currentTime); };
  const handleLoadedMetadata = () => { if (audioRef.current) setDuration(audioRef.current.duration); };
  const handleEnded = () => setPlaying(false);
  const handleSeek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = ratio * duration;
    setCurrentTime(ratio * duration);
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    return `${m}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };
  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="w-full">
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        preload="metadata"
      />
      <div
        className="w-full h-1.5 bg-white/20 rounded-full mb-5 cursor-pointer relative overflow-hidden"
        onClick={handleSeek}
      >
        <div
          className="absolute inset-y-0 left-0 bg-amber-400 rounded-full transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="flex items-center gap-4">
        <button
          onClick={toggle}
          className="flex-shrink-0 w-11 h-11 rounded-full bg-amber-400 hover:bg-amber-300 flex items-center justify-center transition-colors shadow-lg"
          aria-label={playing ? 'Pausar' : 'Reproducir'}
        >
          {playing
            ? <Pause className="w-4 h-4 text-slate-900" strokeWidth={2.5} />
            : <Play className="w-4 h-4 text-slate-900 ml-0.5" strokeWidth={2.5} />
          }
        </button>
        <div className="flex items-center gap-1 text-xs font-mono tabular-nums text-white/60">
          <span className="text-white">{fmt(currentTime)}</span>
          <span>/</span>
          <span>{duration > 0 ? fmt(duration) : '~13:00'}</span>
        </div>
        <div className="flex-1" />
        <button
          onClick={toggleMute}
          className="text-white/50 hover:text-white transition-colors"
          aria-label={muted ? 'Activar sonido' : 'Silenciar'}
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PÁGINA PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
export default function SevenAmLibraryEp0() {
  return (
    <>
      <SEOHead
        title="Ep. 0 — El cerebro que decide sin avisarte | 7AM Library"
        description="Cómo el pensamiento automático gobierna las decisiones ejecutivas de alto riesgo y qué mecanismos pueden auditarlo. Basado en Pensar rápido, pensar despacio, Daniel Kahneman."
      />

      <div className="min-h-screen" style={{ backgroundColor: '#F7F9FB' }}>
        <Navbar />

        {/* ── HEADER PORTADA EDITORIAL ── */}
        <section className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
            <Link
              to="/7am-library"
              className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6 transition-colors text-sm"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              7AM Library
            </Link>

            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
                7AM Library · Episodio 0 de 10
              </p>
              <div className="w-12 h-0.5 bg-amber-400 mb-4" />
              <h1 className="text-2xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-3">
                El cerebro que decide sin avisarte
              </h1>
              <p className="text-base sm:text-xl text-slate-500 font-light tracking-wide leading-relaxed">
                Cómo el pensamiento automático gobierna las decisiones ejecutivas de alto
                riesgo — y qué mecanismos pueden auditarlo.
              </p>
            </div>
          </div>
        </section>

        {/* ── AUDIO PLAYER ── */}
        <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-12">
          <div
            className="rounded-2xl overflow-hidden shadow-xl"
            style={{ background: 'linear-gradient(135deg, #0c1a2e 0%, #162944 60%, #0e2240 100%)' }}
          >
            <div className="h-1 w-full bg-gradient-to-r from-amber-400 to-yellow-500" />

            <div className="px-3 sm:px-10 py-5 sm:py-10">
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 text-amber-300 text-xs font-semibold px-3 py-1.5 rounded-full">
                  <Headphones className="w-3.5 h-3.5" />
                  Episodio 0 · ~13 minutos
                </div>
                <div className="inline-flex items-center gap-2 text-white/40 text-xs">
                  <BookOpen className="w-3.5 h-3.5" />
                  <em>Pensar rápido, pensar despacio</em> · Daniel Kahneman
                </div>
              </div>

              <p className="text-white/70 text-sm sm:text-base leading-relaxed mb-6 italic max-w-2xl">
                &ldquo;Escucha cómo se analiza el mecanismo cognitivo que convierte
                la deliberación ejecutiva en una ilusión de rigor.&rdquo;
              </p>

              <AudioPlayer src="/assets/library/Audios/ep-0.m4a" />
            </div>
          </div>
        </section>

        {/* ── CONTENIDO EDITORIAL ── */}
        <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-5 sm:pb-12">
          <div className="bg-slate-50 rounded-xl sm:rounded-2xl border border-slate-200 px-4 sm:px-8 py-6 sm:py-10 space-y-8 sm:space-y-10">

            {/* 4. REFERENCIA INTELECTUAL */}
            <div className="flex items-start gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3">
              <BookOpen className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-0.5">
                  Conversación basada en
                </p>
                <p className="text-sm font-semibold text-slate-800 leading-snug">
                  Pensar rápido, pensar despacio
                </p>
                <p className="text-xs text-slate-500 mt-0.5">Daniel Kahneman</p>
              </div>
            </div>

            <div className="border-t border-slate-200" />

            {/* 6. IMAGEN */}
            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
                Imagen
              </p>
              <div className="w-12 h-0.5 bg-amber-400 mb-3" />
              <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight mb-1">
                El consenso rápido no es alineación. Es una alerta.
              </h2>
              <p className="text-sm text-slate-500 mb-4 leading-relaxed">
                Cómo el Sistema 1 y el Sistema 2 explican las decisiones que parecen
                rigurosas pero no lo son.
              </p>

              <div className="flex justify-center">
                <img
                  src="/assets/library/Poster/ep-0.png"
                  alt="Infográfico de contraste en dos columnas. Columna izquierda azul marino: Sistema 1 — Instantáneo, Sin esfuerzo, Narrativa cómoda, Domina por defecto. Columna derecha teal: Sistema 2 — Deliberado, Alto costo energético, Escrutinio real, Se activa con fricción. Tres cápsulas inferiores: Efecto Halo, Exceso de Confianza, Anclaje. Frase de cierre: No puedes apagar el Sistema 1. Solo puedes diseñar el proceso para que no decida solo. 7AM Library by EduTech Pulse Lab."
                  className="w-full md:max-w-xl rounded-xl border border-slate-200"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            </div>

            <div className="border-t border-slate-200" />

            {/* 7. CONTEXTO */}
            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
                Contexto
              </p>
              <div className="w-12 h-0.5 bg-amber-400 mb-3" />
              <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight mb-4">
                Contexto de esta conversación
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-3">
                Este episodio es un destilado ejecutivo de{' '}
                <em>Pensar rápido, pensar despacio</em> de Daniel Kahneman.
                No es un resumen del libro — es una conversación que extrae su idea
                más aplicable y la contrasta con situaciones reales de gestión y liderazgo.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-3">
                Este episodio analiza por qué los comités de dirección toman decisiones
                de alto riesgo con rapidez, seguridad y consenso — y por qué esa fluidez
                es exactamente la señal de que algo falló.
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                No se trata de incompetencia. Se trata de cómo el cerebro humano gestiona
                sus recursos cognitivos bajo condiciones de facilidad y familiaridad.
              </p>
            </div>

            {/* 8. POR QUÉ IMPORTA */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 sm:p-6">
              <h3 className="text-base font-bold text-slate-900 mb-3 flex items-center gap-2 tracking-[0.05em] uppercase">
                <Lightbulb className="w-4 h-4 text-amber-600" />
                Por qué importa
              </h3>
              <p className="text-base text-slate-600 leading-relaxed mb-3">
                La certeza rápida y el consenso sin fricciones rara vez son indicadores
                de análisis riguroso. Son síntomas del Sistema 1 operando sin supervisión.
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-3">
                Cuando un comité aprueba una inversión significativa sin que nadie cuestione
                las premisas, no hay alineación real — hay comodidad cognitiva. El cerebro
                rellenó los vacíos de información con narrativas prefabricadas y los presentó
                como conclusiones técnicas.
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                El costo no aparece ese día. Aparece meses después, en la auditoría que
                demuestra que los datos críticos estaban disponibles en esa misma sala
                — y nadie los procesó porque el Sistema 1 no emitió ninguna señal de alerta.
              </p>
            </div>

            {/* 9. IDEA CENTRAL */}
            <div className="border-l-4 border-amber-400 pl-5 py-1">
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
                Idea central
              </p>
              <p className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight leading-snug mb-4">
                La certeza y el consenso rápido no son indicadores de un análisis
                riguroso. Son síntomas de un Sistema 1 operando sin supervisión.
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                La solución no es ordenar a los ejecutivos que piensen mejor. Es diseñar
                el proceso para que el error no se correlacione.
              </p>
            </div>

            {/* 10. CORRECCIÓN EDITORIAL — solo metadata interna, no se muestra al lector */}

            <div className="border-t border-slate-200" />

            {/* 11. APLICACIÓN PRÁCTICA */}
            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
                Aplicación práctica
              </p>
              <div className="w-12 h-0.5 bg-amber-400 mb-3" />
              <h3 className="text-lg sm:text-xl font-semibold text-slate-900 tracking-tight mb-4">
                Aplicación práctica
              </h3>
              <p className="text-base text-slate-600 leading-relaxed mb-4">
                Antes de tu próxima reunión donde se tome una decisión importante —
                aprobación de un proyecto, selección de un proveedor, evaluación de
                una persona — haz esto:
              </p>
              <div className="border-l-4 border-amber-400 pl-5 mb-4 space-y-2">
                <p className="text-base text-slate-700 leading-relaxed font-medium">
                  Pide a cada participante que escriba individualmente su evaluación
                  antes de que alguien hable.
                </p>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Dos minutos. Una hoja. Sin compartir.
                </p>
              </div>
              <p className="text-base text-slate-600 leading-relaxed mb-3">
                Después comparen. Lo que emerge antes del debate es criterio real.
                Lo que emerge después es influencia del que habló primero.
              </p>
              <p className="text-sm text-slate-500 leading-relaxed">
                <strong className="text-slate-600">Consejo:</strong>{' '}
                Elige esta semana una sola reunión y aplica este protocolo. Observa
                qué cambia.</p>
            </div>

            {/* 12. PREGUNTA DE REFLEXIÓN */}
            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3 flex items-center gap-2">
                <HelpCircle className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
                Pregunta de reflexión
              </p>
              <div className="w-12 h-0.5 bg-amber-400 mb-4" />
              <blockquote className="border-l-4 border-slate-300 pl-5 text-base sm:text-lg text-slate-700 leading-relaxed font-light italic space-y-3">
                <p>
                  En tu última decisión de alto riesgo como comité, ¿estaban evaluando
                  de forma independiente los datos que contradecían la premisa?
                </p>
                <p>
                  ¿O estaban construyendo juntos la narrativa más cómoda con la
                  información que ya tenían?
                </p>
              </blockquote>
            </div>

          </div>
        </section>

        <SevenAmSubscribe />

        {/* ── SIGUIENTE EPISODIO ── */}
        <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-5 sm:pb-12">
          <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-200 px-4 sm:px-8 py-5 sm:py-8">
            <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-3">
              Siguiente episodio recomendado
            </p>
            <div className="w-12 h-0.5 bg-amber-400 mb-4" />
            <p className="text-sm text-amber-600 font-semibold mb-1">
              Episodio 1 — Thinking in Systems
            </p>
            <p className="text-sm text-slate-500 mb-2">Donella Meadows</p>
            <p className="text-base text-slate-600 leading-relaxed mb-5">
              El cerebro opera en piloto automático. Ahora: ¿qué ocurre cuando ese
              piloto automático está respondiendo a una estructura organizacional
              diseñada para producir siempre el mismo error?
            </p>
            <Link
              to="/7am-library/episodio-1"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-700 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              Ir al episodio 1
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* ── PIE DE PÁGINA ── */}
        <section className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-12 sm:pb-16">
          <div className="border-t border-slate-200 pt-6 sm:pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold text-slate-600 tracking-[0.2em] uppercase mb-0.5">
                7AM Library by EduTech Pulse Lab
              </p>
              <p className="text-sm text-slate-500">
                No resumimos libros. Destilamos ideas para pensar, decidir y actuar mejor.
              </p>
            </div>
            <Link
              to="/7am-library"
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-amber-600 transition-colors font-medium"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Ver todos los episodios
            </Link>
          </div>
        </section>

      </div>
    </>
  );
}
